import axios, { AxiosError } from 'axios';
import { PROMETHEUS_URL } from '../../const/const';

const getPrometheusUrl = () => PROMETHEUS_URL;

interface PrometheusQueryResponse {
    status: string;
    data: {
        resultType: string;
        result: Array<{
            metric: Record<string, string>;
            value: [number, string];
        }>;
    };
}

interface PrometheusQueryRangeResponse {
    status: string;
    data: {
        resultType: string;
        result: Array<{
            metric: Record<string, string>;
            values: Array<[number, string]>;
        }>;
    };
}

const isPrometheusServerAvailable = async (url: string): Promise<boolean> => {
    try {
        const response = await axios.get(`${url}/api/v1/query`, {
            params: { query: 'up' },
            timeout: 15000
        });
        return response.status === 200;
    } catch (error) {
        console.warn(`Prometheus server ${url} is not available:`, error);
        return false;
    }
};

export const getPrometheusMetric = async (query: string): Promise<number> => {
    const urls = [getPrometheusUrl()];

    for (const url of urls) {
        try {
            const isAvailable = await isPrometheusServerAvailable(url);
            if (!isAvailable) {
                console.warn(`Prometheus server ${url} is not available, trying next...`);
                continue;
            }

            const response = await axios.get<PrometheusQueryResponse>(`${url}/api/v1/query`, {
                params: { query },
                timeout: 15000
            });

            if (response.data.status === 'success' && response.data.data.result.length > 0) {
                return parseFloat(response.data.data.result[0].value[1]);
            }
            return 0;
        } catch (error) {
            console.warn(`Error fetching Prometheus metric from ${url}:`, error);
            continue;
        }
    }

    throw new Error('All Prometheus servers are not available');
};

export const getPrometheusMetricRange = async (
    query: string,
    start: number,
    end: number,
    step: string = '1s'
): Promise<Array<[number, number]>> => {
    const urls = [getPrometheusUrl()];

    for (const url of urls) {
        try {
            const isAvailable = await isPrometheusServerAvailable(url);
            if (!isAvailable) {
                console.warn(`Prometheus server ${url} is not available, trying next...`);
                continue;
            }

            const response = await axios.get<PrometheusQueryRangeResponse>(`${url}/api/v1/query_range`, {
                params: { query, start, end, step },
                timeout: 15000
            });

            if (response.data.status === 'success' && response.data.data.result.length > 0) {
                return response.data.data.result[0].values.map(([timestamp, value]) => [
                    timestamp,
                    parseFloat(value)
                ]);
            }
            return [];
        } catch (error) {
            console.warn(`Error fetching Prometheus metric range from ${url}:`, error);
            continue;
        }
    }

    throw new Error('All Prometheus servers are not available');
};

const MAX_NETWORK_SPEED_BPS = 5 * 1000 * 1000;

export const getServerMetrics = async () => {
    try {
        const [
            cpuUsage,
            ramUsagePercent,
            diskUsagePercent,
            networkDownload,
            networkUpload,
            currentNetworkSpeedBps
        ] = await Promise.all([
            getPrometheusMetric('100 * (1 - avg(rate(node_cpu_seconds_total{mode="idle"}[1m])))'),
            getPrometheusMetric('(1 - (node_memory_MemAvailable_bytes / node_memory_MemTotal_bytes)) * 100'),
            getPrometheusMetric('((node_filesystem_size_bytes{mountpoint="/"} - node_filesystem_avail_bytes{mountpoint="/"}) / node_filesystem_size_bytes{mountpoint="/"}) * 100'),
            getPrometheusMetric('node_network_receive_bytes_total'),
            getPrometheusMetric('node_network_transmit_bytes_total'),
            getPrometheusMetric('sum(rate(node_network_receive_bytes_total[1m]))*8')
        ]);

        const networkLoadPercent = (currentNetworkSpeedBps / MAX_NETWORK_SPEED_BPS) * 100;

        return {
            cpu_usage: Math.max(0, Math.min(100, Math.round(cpuUsage))),
            ram_usage: Math.round(ramUsagePercent),
            hdd_usage: Math.round(diskUsagePercent),
            net_download: networkDownload,
            net_upload: networkUpload,
            net_speed_download: currentNetworkSpeedBps,
            net_speed_upload: 0,
            net_load_percent: Math.max(0, Math.min(100, networkLoadPercent))
        };
    } catch (error) {
        console.error('Error fetching server metrics:', error);
        throw error;
    }
};

export const getCurrentNetworkTraffic = async () => {
    const urls = [getPrometheusUrl()];

    for (const url of urls) {
        try {
            const currentTime = Math.floor(Date.now() / 1000);
            const query = `sum(node_network_receive_bytes_total) by (instance)`;

            const isAvailable = await isPrometheusServerAvailable(url);
            if (!isAvailable) {
                console.warn(`Prometheus server ${url} is not available, trying next...`);
                continue;
            }

            const response = await axios.get<PrometheusQueryResponse>(`${url}/api/v1/query`, {
                params: {
                    query: query,
                    time: currentTime
                },
                timeout: 15000
            });

            let downloadTotal = 0;
            if (response.data.status === 'success' && response.data.data.result.length > 0) {
                downloadTotal = parseFloat(response.data.data.result[0].value[1]);
            }

            return {
                download: downloadTotal,
                upload: downloadTotal * 0.5,
                timestamp: currentTime
            };
        } catch (error) {
            console.warn(`Error fetching current network traffic from ${url}:`, error);
            continue;
        }
    }

    throw new Error('All Prometheus servers are not available');
};
